<script lang="ts" context="module">
  export type CancelationData = { gracePeriod?: string | number; rescheduleFee?: string | number };
</script>

<script lang="ts">
  import { tooltip } from '$lib/directives/tooltip';
  import type { Api_Account, Api_PurchasedProductCoachingSession } from '$lib/types/api/graphql';
  import { extractNodes, formatCurrency, plural, toInteger } from '$lib/utils';
  import IconWrapper, { IconWrapperSize } from '$mds/IconWrapper';
  import InfoIcon from '$mds/InfoIcon/InfoIcon.svelte';
  import { faCalendarClock, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';
  import { dayjs } from '@metafy/dates';

  /** for the settings page, to update tooltip immediately */
  export let previewData: CancelationData | null = null;
  /** for the reschedule page, where the tooltip needs to show the session's reschedule policy */
  export let coachingSession: Api_PurchasedProductCoachingSession | null = null;
  /** for everywhere else, where the tooltip needs to show the coach's current reschedule policy */
  export let coachProfile: Partial<Api_Account> | null = null;

  /** props */
  export let iconOnly = false;
  export let iconSize: IconWrapperSize = 'md';
  export let tooltipInstance: undefined | ReturnType<typeof tooltip> = undefined;

  let policyEl: HTMLElement;
  let iconEl: HTMLElement;

  const niceTimePreview = (hours: number): string => {
    if (hours <= 48) {
      const count = dayjs.duration(hours, 'hours').asHours();
      return plural('hour', count);
    } else if (hours < 168) {
      const count = dayjs.duration(hours, 'hours').asDays();
      return plural('day', count);
    } else {
      const count = dayjs.duration(hours, 'hours').asWeeks();
      return plural('week', count);
    }
  };

  $: if (previewData && previewData.gracePeriod) {
    previewData.gracePeriod = niceTimePreview(toInteger(previewData?.gracePeriod) || 0);
  }

  $: tooltipData = {} as CancelationData;
  $: if (previewData) {
    tooltipData = previewData;
  } else if (coachingSession) {
    tooltipData = {
      gracePeriod: niceTimePreview(extractNodes(coachingSession?.coaching?.product?.policies)[0]?.timeFrame ?? 0),
      rescheduleFee: extractNodes(coachingSession?.coaching?.product?.policies)[0]?.amount
    };
  } else if (coachProfile) {
    tooltipData = {
      gracePeriod: niceTimePreview(coachProfile.sessionCancelationPolicy?.timeFrame ?? 0),
      rescheduleFee: coachProfile.sessionCancelationPolicy?.amount
    };
  }

  const createTooltip = ({ gracePeriod, rescheduleFee }: CancelationData) => {
    const content = `
      <h4 class="text-left">${coachProfile?.name}ʼs Policy:</h4>
      <ul class="text-left list-disc list-outside pl-4 p-2 flex flex-col gap-y-4 text-body-2xs text-ondark-secondary">
        <li>Cancel or reschedule sessions for <strong class="text-ondark-primary">free up to ${gracePeriod}</strong> 
          ahead of the start time.</li>
        <li><strong class="text-ondark-primary">${rescheduleFee ?? 'a '}% of the Session price is</strong> 
          applied as a fee to reschedule or cancel within ${gracePeriod} from start time. </li>
      </ul>
    `;
    if (!tooltipInstance) {
      tooltipInstance = tooltip(iconOnly ? policyEl : iconEl, {
        content,
        allowHTML: true,
        placement: 'top',
        interactive: true,
        hideOnClick: false,
        triggerTarget: policyEl
      });
    }
    tooltipInstance?.instance?.setContent?.(content);
  };

  $: if (policyEl) {
    createTooltip(tooltipData);
  }
</script>

{#if coachProfile || previewData}
  <button
    bind:this={policyEl}
    class="text-ondark-primary text-body-3xs focus-within:ring-ondark-focused group -mx-1 inline-flex cursor-help items-center gap-2 rounded-full px-1 outline-none focus-within:ring-2"
  >
    <IconWrapper icon={faCalendarClock} class=" text-positive-ondark-base" size={iconSize} />
    {#if !iconOnly}
      Cancelation and Reschedule policy
      <InfoIcon bind:node={iconEl} tabindex={-1} asSpan />
    {/if}
  </button>
{/if}
